import { authenticatedHttpClient } from './httpClient';

export interface SalesPersonCommissionReportRow {
  salesPersonId?: string | null;
  salesPersonCode?: string | null;
  salesPersonName: string;
  commissionType: 'venta' | 'cobro' | 'ambos' | 'none';

  saleCommissionPercent: number;
  collectionCommissionPercent: number;

  saleBaseAmount: number;
  collectionBaseAmount: number;

  saleCommissionAmount: number;
  collectionCommissionAmount: number;

  totalCommissionAmount: number;
}

export interface SalesPeopleCommissionReportTotals {
  saleBaseAmountTotal: number;
  collectionBaseAmountTotal: number;
  saleCommissionAmountTotal: number;
  collectionCommissionAmountTotal: number;
  totalCommissionAmountTotal: number;
}

export interface SalesPeopleCommissionReport {
  currency: string;
  generatedAt: string | Date;
  rows: SalesPersonCommissionReportRow[];
  totals: SalesPeopleCommissionReportTotals;
}

export interface SalesPeopleCommissionSummaryQuery {
  activeOnly?: boolean;
  salesPersonId?: string;
  /** Format: YYYY-MM-DD */
  dateFrom?: string;
  /** Format: YYYY-MM-DD */
  dateTo?: string;
}

export interface CommissionReportSaleLine {
  orderId: string;
  saleNumber?: number | null;
  clientName: string;
  clientId?: string | null;
  orderDate?: string | Date | null;
  orderTotal: number;
  commissionPercent: number;
  commissionAmount: number;
}

export interface CommissionReportCollectionMovementLine {
  movementId: string;
  movementType: string;
  movementDate?: string | Date | null;
  amount: number;
  reference?: string | null;
  commissionPercent: number;
  commissionAmount: number;
}

export interface CommissionReportReceivableLine {
  receivableId: string;
  saleId: string;
  saleNumber?: number | null;
  clientName: string;
  clientId?: string | null;
  originalAmount: number;
  balanceAmount: number;
  paidAmountCounted: number;
  status: string;
  commissionPercent: number;
  commissionAmount: number;
  movements: CommissionReportCollectionMovementLine[];
}

export interface SalesPersonCommissionDetailReport {
  currency: string;
  salesPersonId: string;
  salesPersonCode?: string | null;
  salesPersonName: string;
  commissionType: 'venta' | 'cobro' | 'ambos' | 'none';
  dateFrom?: string | null;
  dateTo?: string | null;
  saleCommissionPercent: number;
  collectionCommissionPercent: number;
  saleLines: CommissionReportSaleLine[];
  collectionLines: CommissionReportReceivableLine[];
  saleBaseTotal: number;
  collectionBaseTotal: number;
  saleCommissionTotal: number;
  collectionCommissionTotal: number;
  totalCommissionAmount: number;
}

export const getSalesPeopleCommissionSummaryServer = async (
  organizationId: string,
  query: SalesPeopleCommissionSummaryQuery = {}
): Promise<SalesPeopleCommissionReport> => {
  const params: Record<string, unknown> = {};
  if (query.activeOnly !== undefined) params.active_only = query.activeOnly;
  if (query.salesPersonId) params.sales_person_id = query.salesPersonId;
  if (query.dateFrom) params.date_from = query.dateFrom;
  if (query.dateTo) params.date_to = query.dateTo;

  const res = await authenticatedHttpClient.get<SalesPeopleCommissionReport>(
    `/sales-people/${organizationId}/reports/commission-summary`,
    { params }
  );
  return res;
};

export interface SendCommissionSummaryEmailPayload {
  toEmail: string;
  salesPersonId?: string;
  /** Format: YYYY-MM-DD */
  dateFrom?: string;
  /** Format: YYYY-MM-DD */
  dateTo?: string;
  activeOnly?: boolean;
}

export const getSalesPersonCommissionDetailServer = async (
  organizationId: string,
  salesPersonId: string,
  query: SalesPeopleCommissionSummaryQuery = {}
): Promise<SalesPersonCommissionDetailReport> => {
  const params: Record<string, unknown> = {};
  if (query.activeOnly !== undefined) params.active_only = query.activeOnly;
  if (query.dateFrom) params.date_from = query.dateFrom;
  if (query.dateTo) params.date_to = query.dateTo;

  const res = await authenticatedHttpClient.get<SalesPersonCommissionDetailReport>(
    `/sales-people/${organizationId}/reports/commission-summary/${salesPersonId}/detail`,
    { params }
  );
  return res;
};

export const sendCommissionSummaryEmailServer = async (
  organizationId: string,
  payload: SendCommissionSummaryEmailPayload
): Promise<{ message: string }> => {
  const res = await authenticatedHttpClient.post<{ message: string }>(
    `/sales-people/${organizationId}/reports/commission-summary/email`,
    payload
  );
  return res;
};

